/**
 * Code-table lookups for HL7 v2.5 codes used by the HL7 renderer.
 *
 * Two flavors live here:
 *
 * 1. **Field-keyed primitive coded fields** (e.g. PID-8 sex, PV1-2 patient class) — used by the
 *    `IS`/`ID` and `CWE` renderers when a message arrives without inline display text.
 * 2. **Datatype-component label maps** (address types, telecom use codes, etc.) — used inline
 *    by the per-datatype renderers to label specific component values (XAD-7, XTN-2, etc.).
 *
 * Coverage is intentionally narrow: codes prioritized for our test fixtures and codes commonly
 * populated across HL7 v2 traffic. Source numbers reference the spec's table registry
 * (HL70001, HL70004, etc.).
 *
 * @see https://hl7-definition.caristix.com/v2/HL7v2.5/Tables — HL7 v2.5 user-defined tables
 *   index. Individual tables are linked from each table's docblock below.
 */

// =============================================================================
// Field-keyed primitive coded fields
// =============================================================================

/**
 * HL70001 — Administrative Sex (PID-8).
 * @see https://hl7-definition.caristix.com/v2/HL7v2.5/Tables/0001
 */
const ADMINISTRATIVE_SEX: Record<string, string> = {
  A: 'Ambiguous',
  F: 'Female',
  M: 'Male',
  N: 'Not applicable',
  O: 'Other',
  U: 'Unknown',
};

/**
 * HL70002 — Marital Status (PID-16).
 * @see https://hl7-definition.caristix.com/v2/HL7v2.5/Tables/0002
 */
const MARITAL_STATUS: Record<string, string> = {
  A: 'Separated',
  B: 'Unmarried',
  C: 'Common law',
  D: 'Divorced',
  E: 'Legally Separated',
  G: 'Living together',
  I: 'Interlocutory',
  M: 'Married',
  N: 'Annulled',
  O: 'Other',
  P: 'Domestic partner',
  R: 'Registered domestic partner',
  S: 'Single',
  T: 'Unreported',
  U: 'Unknown',
  W: 'Widowed',
};

/**
 * HL70004 — Patient Class (PV1-2).
 * @see https://hl7-definition.caristix.com/v2/HL7v2.5/Tables/0004
 */
const PATIENT_CLASS: Record<string, string> = {
  B: 'Obstetrics',
  C: 'Commercial Account',
  E: 'Emergency',
  I: 'Inpatient',
  N: 'Not Applicable',
  O: 'Outpatient',
  P: 'Preadmit',
  R: 'Recurring patient',
  U: 'Unknown',
};

/**
 * HL70007 — Admission Type (PV1-4).
 * @see https://hl7-definition.caristix.com/v2/HL7v2.5/Tables/0007
 */
const ADMISSION_TYPE: Record<string, string> = {
  A: 'Accident',
  C: 'Elective',
  E: 'Emergency',
  L: 'Labor and Delivery',
  N: 'Newborn (Birth in healthcare facility)',
  R: 'Routine',
  U: 'Urgent',
};

/**
 * HL70023 — Admit Source (PV1-14).
 * @see https://hl7-definition.caristix.com/v2/HL7v2.5/Tables/0023
 */
const ADMIT_SOURCE: Record<string, string> = {
  '1': 'Physician referral',
  '2': 'Clinic referral',
  '3': 'HMO referral',
  '4': 'Transfer from a hospital',
  '5': 'Transfer from a skilled nursing facility',
  '6': 'Transfer from another health care facility',
  '7': 'Emergency room',
  '8': 'Court/law enforcement',
  '9': 'Information not available',
};

/**
 * HL70052 — Diagnosis Type (DG1-6).
 * @see https://hl7-definition.caristix.com/v2/HL7v2.5/Tables/0052
 */
const DIAGNOSIS_TYPE: Record<string, string> = {
  A: 'Admitting',
  F: 'Final',
  W: 'Working',
};

/**
 * HL70062 — Event Reason (EVN-4).
 * @see https://hl7-definition.caristix.com/v2/HL7v2.5/Tables/0062
 */
const EVENT_REASON: Record<string, string> = {
  '01': 'Patient request',
  '02': 'Physician/health practitioner order',
  '03': 'Census management',
};

/**
 * HL70112 — Discharge Disposition (PV1-36).
 * @see https://hl7-definition.caristix.com/v2/HL7v2.5/Tables/0112
 */
const DISCHARGE_DISPOSITION: Record<string, string> = {
  '01': 'Discharged to home or self care',
  '02': 'Discharged/transferred to another short term general hospital',
  '03': 'Discharged/transferred to skilled nursing facility',
  '04': 'Discharged/transferred to intermediate care facility',
  '05': 'Discharged/transferred to another type of institution',
  '06': 'Discharged/transferred to home under care of organized home health service',
  '07': 'Left against medical advice',
  '08': 'Discharged/transferred to home under care of home IV provider',
  '09': 'Admitted as an inpatient',
  '20': 'Expired',
  '30': 'Still patient',
};

/**
 * Map of `<segment>-<position>` → code-table values. Indexed by where a code appears in the
 * message rather than by the table's spec number, because the renderer knows the field
 * location at lookup time and not the underlying table number.
 */
const FIELD_CODE_TABLES: Record<string, Record<string, string>> = {
  'PID-8': ADMINISTRATIVE_SEX,
  'PID-16': MARITAL_STATUS,
  'PV1-2': PATIENT_CLASS,
  'PV1-4': ADMISSION_TYPE,
  'PV1-14': ADMIT_SOURCE,
  'PV1-36': DISCHARGE_DISPOSITION,
  'DG1-6': DIAGNOSIS_TYPE,
  'EVN-4': EVENT_REASON,
};

/** Look up the human-readable display for a short code at a given segment+field position. */
export const lookupHl7Code = (
  segmentId: string,
  fieldPosition: number,
  code: string,
): string | undefined => FIELD_CODE_TABLES[`${segmentId}-${fieldPosition}`]?.[code];

// =============================================================================
// Datatype-component label maps
// =============================================================================

/**
 * HL70190 — Address Type. Used by the XAD renderer to label XAD-7.
 * @see https://hl7-definition.caristix.com/v2/HL7v2.5/Tables/0190
 */
export const ADDRESS_TYPE_LABELS: Record<string, string> = {
  B: 'Business',
  BA: 'Bad address',
  BI: 'Billing',
  C: 'Current / Temporary',
  H: 'Home',
  L: 'Legal',
  M: 'Mailing',
  N: 'Birth address',
  O: 'Office / Business',
  P: 'Permanent',
};

/**
 * HL70201 — Telecommunication Use Code. Labels XTN-2.
 * @see https://hl7-definition.caristix.com/v2/HL7v2.5/Tables/0201
 */
export const TELECOM_USE_LABELS: Record<string, string> = {
  ASN: 'Answering Service',
  BPN: 'Beeper',
  EMR: 'Emergency',
  NET: 'Network',
  ORN: 'Other Residence',
  PRN: 'Primary Residence',
  PRS: 'Personal',
  VHN: 'Vacation Home',
  WPN: 'Work',
};

/**
 * HL70202 — Telecommunication Equipment Type. Labels XTN-3.
 * @see https://hl7-definition.caristix.com/v2/HL7v2.5/Tables/0202
 */
export const TELECOM_EQUIPMENT_LABELS: Record<string, string> = {
  BP: 'Beeper',
  CP: 'Cellular',
  FX: 'Fax',
  Internet: 'Email',
  MD: 'Modem',
  PH: 'Telephone',
  SAT: 'Satellite',
  TDD: 'TDD',
  TTY: 'TTY',
  X400: 'X.400',
};

/**
 * HL70305 — Person Location Type. Labels PL-6.
 * @see https://hl7-definition.caristix.com/v2/HL7v2.5/Tables/0305
 */
export const PERSON_LOCATION_TYPE_LABELS: Record<string, string> = {
  C: 'Clinic',
  D: 'Department',
  H: 'Home',
  N: 'Nursing Unit',
  O: 'Provider Office',
  P: 'Phone',
  S: 'SNF',
  W: 'Warehouse',
};

/**
 * The XTN-3 equipment code that signals "this telecom address is an email". Per HL7 v2.5
 * spec, XTN-4 (Communication Address) carries the email only when XTN-3 = `Internet`; for
 * any other equipment type it's some other communication address (X.400, etc.).
 *
 * @see https://hl7-definition.caristix.com/v2/HL7v2.5/Fields/XTN.3 — XTN-3 Equipment Type
 * @see https://hl7-definition.caristix.com/v2/HL7v2.5/Fields/XTN.4 — XTN-4 Communication Address
 */
export const TELECOM_INTERNET_EQUIPMENT_CODE = 'Internet';
